/* eslint-disable eslint-comments/disable-enable-pair -- remove warning */
/* eslint-disable camelcase -- remove warning */
import { ReadLoyaltyBenefitsVx, CreateLoyaltyBenefitEnrollmentStatusVx } from '../constants';
import { cleanBenefitOrProductName } from '../utilities/benefit-name-product-name-clean';
import getParameterByName from '../utilities/getParameterByName';

/**
 * Utility function that returns the endpoint for fetchye call based on path
 */
export function getEndpoint(domain, path) {
  if (path === ReadLoyaltyBenefitsVx) {
    // if (domain.includes('qa') || domain.includes('dev')) return `${domain}/aaluri/${ReadLoyaltyBenefitsVx}`; // temporary for e2 testing
    return `${domain}/${ReadLoyaltyBenefitsVx}`;
  }
  if (path === CreateLoyaltyBenefitEnrollmentStatusVx) {
    // if (domain.includes('qa') || domain.includes('dev')) return `${domain}/rjindam/${CreateLoyaltyBenefitEnrollmentStatusVx}`; // temporary for e2 testing
    return `${domain}/${CreateLoyaltyBenefitEnrollmentStatusVx}`;
  }
  return null;
}

/**
 * Utility function that returns request body for one data ReadLoyaltyBenefits api call
 * Sequence of responses from ReadLB.v2:
 *  cardProductName takes precedence to accountToken
 *  accountToken and cardProductName are different -> returns logged out response for cardProductName
 *  only accountToken -> returns logged in response for card product linked to accountToken
 *  only cardProductName -> returns logged out response for cardProductName
 */
export function getReadBenefitsRequestBody(accountToken, cardName, login, productTypeValue, locale) {
  if (login) {
    let productType;
    if (productTypeValue === 'CARD_PRODUCT') {
      productType = 'AEXP_CARD_ACCOUNT';
    } else {
      productType = productTypeValue;
    }
    return {
      accountToken,
      locale: locale || 'en-US',
      productType,
    };
  }
  return {
    accountToken,
    cardProductName: cardName,
    locale: locale || 'en-US',
  };
}

/**
 * Utility function that returns the request body for CreateLoyaltyBenefitEnrollmentStatus api call
   below constants are intentionally not camelCase to match the API response
*/

export function getEnrollmentRequestBody(accountToken, sorBenefitId, locale, options = {}) {
  const membershipNumber = options?.membershipNumber;
  const selectionCode = options?.selectionCode;
  const address_line_1 = options?.memberAddress1;
  const address_line_2 = options?.memberAddress2;
  const address_line_3 = options?.memberAddress3;
  const city = options?.memberCity;
  const date_of_birth = options?.memberDOB;
  const country = options?.memberCountry;
  const email = options?.memberEmail;
  const passport_full_name = options?.memberPassport;
  const phone_number = options?.memberPhoneNumber;
  const country_code = options?.memberPhoneCountry;
  const postal_code = options?.memberPostalCode;
  const state = options?.memberState;
  const first_name = options?.memberFirstName;
  const last_name = options?.memberLastName;
  const title_name = options?.memberNameTitle;
  const benefitName = cleanBenefitOrProductName(options?.benefitName);
  const cardName = cleanBenefitOrProductName(options?.cardname);
  const layoutType = options?.layoutType;
  const subscriptionId = options?.subscriptionId;
  const isOpenWindow = layoutType === 'OPENWINDOW';

  const analyticsData = {
    benefitName,
    cardName,
    channel: 'web',
    locale,
  };

  return {
    accountToken,
    benefit: sorBenefitId,
    details: { existing_membership_number: membershipNumber, address_line_1, address_line_2, address_line_3, city, country, date_of_birth, email, passport_full_name, phone_number, country_code, postal_code, state, first_name, last_name, title_name },
    ...selectionCode && { selection_code: selectionCode },
    ...isOpenWindow && { subscriptionId }, // only send subscriptionId if eligible for OPENWINDOW enrollment
    analyticsData,
  };
}

export function getEnrollmentRequestBodyMilesHeadstart(accountToken, sorBenefitId, inputNumber, locale, options = {}) {
  const membershipNumber = options?.membershipNumber;
  const selectionCode = options?.selectionCode;
  const benefitName = cleanBenefitOrProductName(options?.benefitName);
  const cardName = cleanBenefitOrProductName(options?.cardname);
  const layoutType = options?.layoutType;
  const subscriptionId = options?.subscriptionId;
  const isOpenWindow = layoutType === 'OPENWINDOW';

  const analyticsData = {
    benefitName,
    cardName,
    channel: 'web',
    locale,
  };

  return {
    accountToken,
    benefit: sorBenefitId,
    miles: inputNumber,
    ...membershipNumber && { details: { existing_membership_number: membershipNumber } },
    ...selectionCode && { selection_code: selectionCode },
    ...isOpenWindow && { subscriptionId }, // only send subscriptionId if eligible for OPENWINDOW enrollment
    analyticsData,
  };
}

/**
 * use function name passed in to determine which payload schema to use, then populate account token and benefitId from Url (if needed)
*/
export const payloadsByFunctionName = (accountToken, trackerLimit, benefitId, locale) => ({
  'ReadLoyaltyBenefitsTrackers.v2': {
    body: [
      {
        accountToken,
        locale,
        trackers: [
          {
            benefitId,
            periodType: 'CURRENT',
          },
        ],
      },
    ],
  },
  'ReadBestLoyaltyBenefitsTrackers.v1': {
    body: [
      {
        accountToken,
        locale,
        limit: getParameterByName('limit') || trackerLimit || 'ALL',
      },
    ],
  },
});

/**
 * Utility function that returns request body for one data ReadBestLoyaltyBenefitsTrackers api call
 */
export function getReadBestLoyaltyBenefitsTrackers(accountToken, functionName, trackerLimit, benefitId, locale) {
  const payloads = payloadsByFunctionName(accountToken, trackerLimit, benefitId, locale);
  // return correct payload schema for the function name passed in
  return payloads[functionName] || payloads['ReadBestLoyaltyBenefitsTrackers.v1'];
}

/**
 * Utility function that returns request body for one data ReadLoyaltyBenefitsTrackers.v2 api call
 */
export function getReadLoyaltyBenefitsTrackers(accountToken, functionName, benefitId, locale) {
  const payloads = payloadsByFunctionName(accountToken, benefitId, locale);
  // return correct payload schema for the function name passed in
  return payloads[functionName] || payloads['ReadLoyaltyBenefitsTrackers.v2'];
}

export const environmentEndpointsConfig = (useLocalTrackerData, faasApiUrl) => ({
  e0: {
    rootUrl: useLocalTrackerData ? faasApiUrl : 'https://functions-staging-qa.aexp.com',
    forkedBranches: {
      ReadLoyaltyBenefitsTrackers: useLocalTrackerData ? '' : '/rjind7',
      ReadBestLoyaltyBenefitsTrackers: useLocalTrackerData ? '' : '/mmekanab',
    },
  },
  e1: {
    rootUrl: faasApiUrl,
    forkedBranches: {
      ReadLoyaltyBenefitsTrackers: '/rjind7',
      ReadBestLoyaltyBenefitsTrackers: '/mmekanab',
    },
  },
  e2: {
    rootUrl: faasApiUrl,
    forkedBranches: {
      ReadLoyaltyBenefitsTrackers: '',
      ReadBestLoyaltyBenefitsTrackers: '',
    },
  },
});

export const getEnvironment = (isLocalhost, localeFilename) => {
  if (localeFilename === 'integration') {
    if (isLocalhost) {
      return 'e0';
    } return 'e1';
  }
  if (localeFilename === 'qa') {
    return 'e2';
  }
  // if not integration or qa
  return 'e3';
};

export const getApiEndpoint = (useLocalTrackerData, localeFilename, faasApiUrl, functionFriendlyName) => {
  const isLocalhost = faasApiUrl?.includes('localhost');
  const thisEnvironment = getEnvironment(isLocalhost, localeFilename);
  const environmentEndpointConfig = environmentEndpointsConfig(useLocalTrackerData, faasApiUrl);
  // get api config for this environment, if e3/null, default to faasApiUrl with no forked branches
  const rootUrl = environmentEndpointConfig?.[thisEnvironment]?.rootUrl || faasApiUrl;
  const forkedBranch = environmentEndpointConfig?.[thisEnvironment]?.forkedBranches?.[functionFriendlyName] || '';

  return `${rootUrl}${forkedBranch}`;
};
